'use client';

import EmojiText from '@/components/EmojiText/EmojiText';
import { NavigationItemMetaData } from '@/components/Navigation/NavigationItem';
import { mdiChevronDown } from '@mdi/js';
import Icon from '@mdi/react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { FC, ReactNode, useMemo, useState } from 'react';
import {
	Button,
	Header,
	Menu,
	MenuItem,
	MenuSection,
	MenuTrigger,
	Popover,
	Separator,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';

export type MainNavigationItemSource = {
	displayName: string;
	children?: ReactNode;
	url?: string;
	metadata?: Partial<NavigationItemMetaData>;
	subItems?: MainNavigationItemSource[];
};

const MainNavigationItem: FC<{
	className?: string;
	item: MainNavigationItemSource;
	subItems?: MainNavigationItemSource[];
}> = ({ className, item, subItems }) => {
	const pathname = usePathname();

	const [isOpen, setIsOpen] = useState(false);

	const styles = twMerge(
		'shadow-border-bottom-sm flex gap-1 items-center rounded-full after:rounded-full bg-white dark:bg-primary-950 px-4 py-1.5 text-lg font-bold text-primary-700 dark:text-white transition-all active:opacity-95 hover:opacity-80',
		className,
	);

	const isActive =
		(!!item.url && pathname?.startsWith(item.url)) ||
		subItems?.some(
			(subItem) =>
				'url' in subItem &&
				typeof subItem.url !== 'undefined' &&
				pathname?.startsWith(subItem.url),
		);

	return (
		<>
			{!!item && !!item.url && !subItems && (
				<Link href={item.url} className={styles}>
					{item?.children ?? item.displayName}
					{isActive && <MainNavigationItemIsActiveIndicator />}
				</Link>
			)}
			{!!subItems && !item.url && (
				<MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
					<Button
						aria-label={item.displayName}
						className={styles}
						aria-current={isActive}>
						<>
							{item.displayName}
							<Icon
								path={mdiChevronDown}
								size={0.75}
								className={`-me-1 ${isOpen ? 'rotate-180' : 'rotate-0'} transition-all`}
							/>
							{isActive && (
								<MainNavigationItemIsActiveIndicator />
							)}
						</>
					</Button>

					<MainNavigationItemPopoverMenu>
						{subItems.map((subItem) => (
							<MainNavigationItemPopoverMenuItem
								key={`${subItem.displayName}${subItem?.url}`}
								{...subItem}
							/>
						))}
					</MainNavigationItemPopoverMenu>
				</MenuTrigger>
			)}
		</>
	);
};

const MainNavigationItemPopoverMenu: FC<{
	children: ReactNode;
}> = ({ children }) => (
	<Popover
		placement="top"
		offset={4}
		className={twMerge(
			'w-64 shadow-border-bottom-sm z-50 rounded-xl shadow-xl after:rounded-xl',
			'bg-gradient-to-b to-primary-50/85 from-white/85 dark:from-primary-950/90 dark:to-primary-950/80 backdrop-blur-md',
		)}>
		<Menu className="max-h-[calc(100dvh-4.5rem)] overflow-y-auto rounded-xl p-1">
			{children}
		</Menu>
	</Popover>
);

const MainNavigationItemPopoverMenuItem: FC<MainNavigationItemSource> = ({
	displayName,
	url,
	metadata,
	children,
	subItems,
}) => {
	const router = useRouter();

	const hasSubItems = useMemo(
		() => subItems && subItems.length > 0,
		[subItems],
	);

	if (hasSubItems)
		return (
			<MenuSection>
				<Separator className="mx-2 mt-2 h-0.5 bg-primary-900/20 dark:bg-white/10" />
				<Header className="px-4 pb-1 pt-4 text-xl font-bold text-primary-950 dark:text-primary-50">
					<span aria-label={`${displayName}. Sectie.`}>
						{children ?? displayName}{' '}
						<span className="sr-only">. Sectie.</span>
					</span>
				</Header>
				{subItems?.map((subItem) => (
					<MainNavigationItemPopoverMenuItem
						key={`${subItem.displayName}${subItem?.url}`}
						{...subItem}
					/>
				))}
			</MenuSection>
		);

	return (
		<MenuItem
			className="rounded-lg"
			onAction={() => {
				if (url) router.push(url);
			}}>
			{({ isSelected }: { isSelected: boolean }) => (
				<div
					aria-label={`${displayName}. Koppeling.`}
					className={twMerge(
						'flex items-center',
						'select-none cursor-pointer shadow-border-bottom-sm flex gap-3 rounded-lg px-4 py-2 text-lg font-bold text-primary-800 transition-all after:rounded-lg after:opacity-0 active:opacity-95 dark:text-white',
						'hover:bg-primary-500 hover:dark:bg-primary-700 hover:text-white hover:after:opacity-100',
						isSelected &&
							'bg-primary-500 text-white after:opacity-100',
					)}>
					<span aria-hidden className="min-w-5">
						{!!metadata?.emoji && (
							<EmojiText>{metadata.emoji}</EmojiText>
						)}
					</span>
					{displayName}
				</div>
			)}
		</MenuItem>
	);
};

const MainNavigationItemIsActiveIndicator: FC = () => (
	<div
		aria-label="Hier ben je nu"
		className="absolute bottom-1 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-primary-700 dark:bg-white"
	/>
);

export default MainNavigationItem;
